import { usePrevious } from '@lemonade-hq/cdk';
import type { WizardComponentAdapterProps } from '@lemonade-hq/chat-wizard-client-base';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import type { FormattedText, UniclientTitleMode } from '@lemonade-hq/uniclient-chat';
import { TypingTicker, TypingTickerCursor } from '@lemonade-hq/uniclient-theme';
import { AnimatePresence } from 'motion/react';
import { useMemo } from 'react';
import { FRAME_TRANSITION_DURATION } from '../animators/durations';
import { getFormattedTextKey, parseFormattedTitle } from '../utils/parseFormattedTitle';
import { ReasoningText } from './ReasoningText';

const CURSOR_ENTER_DELAY: Milliseconds = 220;
const MIN_CHARS_PER_ITERATION = 1;
const MIN_TYPING_INTERVAL = 5;
const MAX_CHARS_PER_ITERATION = 1;
const MAX_TYPING_INTERVAL = 7;
export const TYPING_TIMEOUT_MULTIPLIER = (MAX_TYPING_INTERVAL / MAX_CHARS_PER_ITERATION) * 1.2;

export type TitleContentProps = {
  readonly loading?: boolean;
  readonly titleKey?: string;
  readonly onAction?: WizardComponentAdapterProps['onAction'];
} & (
  | {
      readonly mode: 'typing';
      readonly titleText: FormattedText;
    }
  | { readonly mode: 'reasoning'; readonly titleText: string }
);

interface TypingTickerWrapperProps {
  readonly children: FormattedText;
  readonly onAction?: WizardComponentAdapterProps['onAction'];
}

const TypingTickerWrapper: React.FC<TypingTickerWrapperProps> = ({ children: title, onAction }) => {
  const { parsedNodes, strippedText } = useMemo(() => parseFormattedTitle(title, onAction), [title, onAction]);

  return (
    <TypingTicker
      cursor={{ entranceDelay: CURSOR_ENTER_DELAY }}
      delayBetweenDifferentTexts={FRAME_TRANSITION_DURATION + CURSOR_ENTER_DELAY}
      maxCharsPerIteration={MAX_CHARS_PER_ITERATION}
      maxTypingInterval={MAX_TYPING_INTERVAL}
      minCharsPerIteration={MIN_CHARS_PER_ITERATION}
      minTypingInterval={MIN_TYPING_INTERVAL}
      preventTextJump
      text={strippedText}
    >
      {parsedNodes}
    </TypingTicker>
  );
};

export const TitleContent = ({ loading, mode, titleText, onAction, titleKey }: TitleContentProps): React.ReactNode => {
  let content: React.ReactNode;
  const previousMode = usePrevious<UniclientTitleMode>(mode, 'typing');
  const firstReasoningRender = previousMode === 'typing' && mode === 'reasoning';
  const componentKey = useMemo(() => getFormattedTextKey(titleText), [titleText]);

  if (mode === 'reasoning') {
    content = <ReasoningText delayAnimation={firstReasoningRender} key={componentKey} text={titleText} />;
  } else if (loading) {
    content = <TypingTickerCursor blinking key={componentKey} />;
  } else {
    content = (
      <TypingTickerWrapper key={titleKey ?? componentKey} onAction={onAction}>
        {titleText}
      </TypingTickerWrapper>
    );
  }

  return <AnimatePresence mode="wait">{content}</AnimatePresence>;
};
